<template>
  <div class="relative page-container  bg-gray-900 text-white">
    <!-- Hero Section - Now with more dynamic energy -->      

    <section class="w-full px-[12.5%] min-h-screen flex items-center relative overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black md:px-12 py-20  shadow-inner animate-shadowGrow">
      <div class="absolute inset-0 bg-[url('@/assets/hero.jpeg')] bg-cover bg-center opacity-5 blur-sm z-10"></div>
      <div class="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-gray-900 -z-10"></div>
      
      <div class="max-w-7xl  mx-auto gap-16 items-center z-50">
        <!-- Text Content - More impactful typography -->
        <div class="space-y-8 animate-fade-in">
          <h1 class="text-5xl holographic-text md:text-6xl font-bold leading-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-300 to-pink-300">
            Ben Chiboub<br/>
            <span class="text-shadow-lg shadow-purple-500/30">Hédi</span>
          </h1>
          
          <p class="text-xl md:text-2xl text-gray-300 max-w-xl leading-relaxed">
            Creativity isn’t dead.<br/>
            It’s just AI-assisted now.
          </p>
          
          <div class="flex flex-wrap gap-4 mt-8">
            <router-link to="/about" class="group">
              <button class="px-8 py-3.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95 group-hover:bg-gradient-to-l">
                Explore My World →
              </button>
            </router-link>
            
            <router-link to="/resources" class="group">
              <button class="px-8 py-3.5 border-2 border-purple-400/50 rounded-xl font-semibold text-lg hover:bg-purple-900/30 transition-all duration-300 transform hover:scale-105 active:scale-95 group-hover:border-purple-300">
                See My Work
              </button>
            </router-link>
          </div>
        </div>

      </div>
    </section>

    <!-- About Section - More engaging layout -->
    <section id="about" class="py-24 w-full px-[12.5%] bg-gradient-to-b from-gray-900 to-gray-800">
      <div class="max-w-6xl mx-auto">
        <div class="grid md:grid-cols-2 gap-16 items-center">
          <div class="space-y-6 animate-slide-in">
            <h2 class="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-300">
              The Creative Mind
            </h2>
            <p class="text-xl text-gray-300 leading-relaxed">
              I'm <span class="font-bold text-white">Hédi Ben Chiboub</span>, a multidisciplinary creator who thrives at the intersection of technology and artistry. With a background in art, web development and game design, I bring unique perspectives to every project.
            </p>
            <p class="text-xl text-gray-300 leading-relaxed">
              My work is driven by a passion for crafting experiences that resonate on both technical and emotional levels - whether through clean code, evocative visuals, or thoughtful poetry.
            </p>
            <div class="pt-4">
              <router-link to="/about" class="abt inline-flex items-center text-purple-300 hover:text-pink-300 group transition-colors">
                <span class="mr-2 font-medium">Dive deeper into my journey</span>
                <span class="group-hover:translate-x-1 transition-transform">→</span>
              </router-link>
            </div>
          </div>
          
          <div class="grid grid-cols-2 gap-4 animate-fade-in">
            <div class="bg-gray-800/50 p-6 rounded-xl border border-gray-700 hover:border-purple-400/30 transition-all hover:-translate-y-1">
              <div class="text-3xl text-purple-400 mb-3">💻</div>
              <h3 class="text-xl font-bold mb-2">Web Development</h3>
              <p class="text-gray-400">Modern, responsive websites with cutting-edge tech</p>
            </div>
            <div class="bg-gray-800/50 p-6 rounded-xl border border-gray-700 hover:border-pink-400/30 transition-all hover:-translate-y-1">
              <div class="text-3xl text-pink-400 mb-3">🎮</div>
              <h3 class="text-xl font-bold mb-2">Game Design</h3>
              <p class="text-gray-400">Immersive gameplay</p>
            </div>
            <div class="bg-gray-800/50 p-6 rounded-xl border border-gray-700 hover:border-blue-400/30 transition-all hover:-translate-y-1">
              <div class="text-3xl text-blue-400 mb-3">🖌️</div>
              <h3 class="text-xl font-bold mb-2">Digital Art</h3>
              <p class="text-gray-400">Expressive visuals that tell stories</p>
            </div>
            <div class="bg-gray-800/50 p-6 rounded-xl border border-gray-700 hover:border-yellow-400/30 transition-all hover:-translate-y-1">
              <div class="text-3xl text-yellow-400 mb-3">📜</div>
              <h3 class="text-xl font-bold mb-2">Islamic Teaching</h3>
              <p class="text-gray-400">Sharing knowledge with clarity and authenticity</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Featured Projects - More visual impact -->
    <section id="projects" class="py-24 w-full px-[12.5%] bg-gray-800/30">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-16 animate-fade-in">
          <h2 class="text-4xl font-bold mb-4">Selected <span class="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-300">Creations</span></h2>
          <p class="text-xl text-gray-400 max-w-3xl mx-auto">Projects where I've poured my passion into pixels and code</p>
        </div>
      
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <a v-for="project in projects" :key="project.id" :href="project.link" target="_blank" class="group relative overflow-hidden rounded-2xl bg-gray-800 border border-gray-700 hover:border-purple-400/50 transition-all duration-500 hover:-translate-y-2">
            <div class="relative overflow-hidden h-60">
              <img :src="getImagePath(project.img)" alt="Project" class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105">
              <div class="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-transparent to-transparent"></div>
            </div>
            <div class="p-6">
              <h3 class="text-2xl font-bold mb-2">{{ project.title }}</h3>
              <p class="text-gray-400 mb-4">{{ project.description }}</p>
              <a :href="project.link" target="_blank" class="inline-flex items-center text-purple-300 hover:text-pink-300 group transition-colors">
                <span class="mr-2 font-medium">Visit website</span>
                <span class="group-hover:translate-x-1 transition-transform">→</span>
              </a>
            </div>
          </a>
        </div>
        
        <div class="text-center mt-16 animate-fade-in">
          <router-link to="/resources" class="inline-flex items-center px-8 py-3.5 bg-gray-700/50 border border-gray-600 rounded-xl font-medium hover:bg-gray-600/50 hover:border-purple-400/30 transition-all duration-300 group">
            <span class="mr-2">View All Projects</span>
            <span class="group-hover:translate-x-1 transition-transform">→</span>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Latest Articles - More engaging cards -->
    <section id="home-articles" class="py-24 w-full px-[12.5%] bg-gradient-to-b from-gray-800 to-gray-900">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-16 animate-fade-in">
          <h2 class="text-4xl font-bold mb-4">Recent <span class="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300">Articles</span></h2>
          <p class="text-xl text-gray-400 max-w-3xl mx-auto">Thoughts on technology, creativity, and everything in between</p>
        </div>
        
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <router-link v-for="post in latestPosts" :key="post.id"  :to="{ name: 'blogPostView', params: { id: post.id }}"  class="group relative overflow-hidden rounded-2xl bg-gray-800/50 backdrop-blur-sm border border-gray-700 hover:border-blue-400/30 transition-all duration-500 hover:-translate-y-2">
            <div class="p-6">
              <div class="flex items-center gap-3 mb-4">
                <div class="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 flex items-center justify-center text-white">
                  {{ post.emoji || "✍️" }}
                </div>
                <span class="text-sm text-gray-400">3 days ago</span>
              </div>
              <h3 class="text-2xl font-bold mb-3 group-hover:text-blue-300 transition-colors">{{ post.title }}</h3>
              <p class="text-gray-400 mb-6">{{ post.preview }}</p>
              <router-link :to="{ name: 'blogPostView', params: { id: post.id }}" class="inline-flex items-center text-blue-300 hover:text-cyan-300 group transition-colors">
                <span class="mr-2 font-medium">Read article</span>
                <span class="group-hover:translate-x-1 transition-transform">→</span>
              </router-link>
            </div>
          </router-link>
        </div>
      </div>
      <div class="text-center mt-16 animate-fade-in">
          <router-link to="/blog/1" class="inline-flex items-center px-8 py-3.5 bg-gray-700/50 border border-gray-600 rounded-xl font-medium hover:bg-gray-600/50 hover:border-purple-400/30 transition-all duration-300 group">
            <span class="mr-2">View All Articles</span>
            <span class="group-hover:translate-x-1 transition-transform">→</span>
          </router-link>
        </div>
    </section>

    <!-- Art Gallery - More immersive grid -->
    <section id="home-gallery" class="py-24 w-full px-[12.5%] bg-gray-900">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-16 animate-fade-in">
          <h2 class="text-4xl font-bold mb-4">Visual <span class="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 to-red-300">Expressions</span></h2>
          <p class="text-xl text-gray-400 max-w-3xl mx-auto">Where color, form, and meaning converge</p>
        </div>
        
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div v-for="art in artPieces" :key="art.id" class="group relative aspect-square overflow-hidden rounded-xl bg-gray-800 hover:bg-gray-700 transition-all duration-300">
            <img :src="art.thumbnail" alt="Art" class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105">
            <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
              <div>
                <h3 class="text-lg font-bold text-white">{{ art.title }}</h3>
                <p class="text-gray-300 text-sm">Digital Art • 2023</p>
              </div>
            </div>
          </div>
        </div>
        
        <div class="text-center mt-16 animate-fade-in">
          <router-link to="/gallery" class="inline-flex items-center px-8 py-3.5 bg-gray-800 border border-gray-700 rounded-xl font-medium hover:bg-gray-700 hover:border-pink-400/30 transition-all duration-300 group">
            <span class="mr-2">Explore Full Gallery</span>
            <span class="group-hover:translate-x-1 transition-transform">→</span>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Contact CTA - More compelling action -->
    <section id="home-cta" class="py-24 w-full px-[12.5%] bg-gradient-to-br from-gray-900 to-purple-900/50">
      <div class="max-w-4xl mx-auto text-center animate-fade-in">
        <h2 class="text-4xl font-bold mb-6">Let's Create <span class="bg-clip-text text-transparent bg-gradient-to-r from-purple-300 to-pink-300">Something Remarkable</span></h2>
        <p class="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
          Whether you have a project in mind, want to collaborate, or just chat about creative possibilities - I'd love to hear from you.
        </p>
        <div class="flex flex-wrap justify-center gap-4 cta-btns">
          <router-link to="/about" class="px-8 py-3.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95">
            Get In Touch
          </router-link>
          <a href="mailto:benchiboub.hedi@gmail.com" class="px-8 py-3.5 border-2 border-purple-400/50 rounded-xl font-semibold text-lg hover:bg-purple-900/30 transition-all duration-300 transform hover:scale-105 active:scale-95">
            Email Me
          </a>
        </div>
      </div>
    </section>

    <footer class="w-full px-[12.5%] py-12 border-t border-gray-800 bg-gray-900/50">
      <div class="max-w-6xl mx-auto">
        <div class="flex flex-col md:flex-row justify-between items-center">
          <div class="mb-6 md:mb-0">
            <h2 class="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300 mb-2">
              Hédi Ben Chiboub
            </h2>
            <p class="text-gray-400">Welcome to my personal website.</p>
          </div>
          <div class="flex gap-4">
            <a href="#" class="text-gray-400 hover:text-blue-300 transition-colors">
              <span class="sr-only">Twitter</span>
              <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
              </svg>
            </a>
            <a href="#" class="text-gray-400 hover:text-blue-300 transition-colors">
              <span class="sr-only">GitHub</span>
              <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path fill-rule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clip-rule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
        <div class="mt-8 pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
          © 2025 Hédi Ben Chiboub. All rights reserved.
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import allBlogPosts from '@/assets/Posts/blogPosts'


const projects = [
{
    id: 1,
    title: 'BlockTheBeat',
    description: 'AI-powered tool to remove background music from videos and audio',    category: 'app',
    link: 'https://blockthebeat.com/',
    tags: ['development', 'open-source', 'ai', 'productivity'],
    img: "blockthebeat"
  },
  {
    id: 2,
    title: 'Hadeeth.live',
    description: 'Your source for authentic Hadith. Explore, search, and learn from the teachings of Prophet Muhammad (ﷺ).',
    category: 'app',
    tags: ['privacy', 'ai', 'organization'],
    link: 'https://www.hadeeth.live/',
    tags: ['islam', 'hadith', 'sunnah', 'knowledge'],
    img: "hadeethlive"
  },
  {
    id: 3,
    title: 'AlQuraan.live',
    description: 'Listen, read, and explore the Holy Quran. Your comprehensive Quran companion with translations, tafsir, and recitations.',
    category: 'app',
    link: 'https://www.alquraan.live/',
    tags: ['islam', 'quran', 'recitation', 'tafsir', 'translation', 'knowledge'],
    img: "alquraanlive"
  },
];
const getImagePath = (baseName) => {
  try {
    return require(`@/assets/Gallery/${baseName}.jpeg`);
  } catch {
    try {
      return require(`@/assets/Gallery/${baseName}.jpg`);
    } catch {
      try {
        return require(`@/assets/Gallery/${baseName}.png`);
      } catch {
        return require('@/assets/Gallery/pho7.png');
      }
    }
  }
};

const latestPosts = computed(() => {
  // Create a shallow copy before sorting to avoid mutating the original imported array
  return [...allBlogPosts]
    .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort by date, newest first
    .slice(0, 3); // Get the first 3 (which are the newest)
});

const customIds = [13, 16, 20, 22, 28, 32, 38, 12]; // Your custom ID list
const artPieces = customIds.map((id, i) => ({
    id: `ai${id}`,
    title: 'Untitled',
    description: "Untitled.Desciption.here",
    type: 'image',
    category: 'Ai',
    src: getImagePath(`ai${id}`),
    thumbnail: getImagePath(`ai${id}`),
    date: `2025-${Math.floor(Math.random() * 12) + 1}-${Math.floor(Math.random() * 28) + 1}`
  }))

</script>

<style scoped>
/* Modern Animations */
@keyframes fade-in {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes slide-in {
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes pulse-slow {
  0%, 100% { opacity: 0.1; }
  50% { opacity: 0.2; }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

@keyframes float-slow {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.animate-fade-in {
  animation: fade-in 1s ease-out forwards;
}

.animate-slide-in {
  animation: slide-in 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.animate-pulse-slow {
  animation: pulse-slow 6s ease-in-out infinite;
}

.animate-float {
  animation: float 8s ease-in-out infinite;
}

@keyframes holographic {
  0% {
    background-position: 0%;
  }
  50% {
    background-position: 200%;
  }
  100% {
    background-position: 0%;
  }
}

.holographic-text {
  font-size: 3rem;
  background: linear-gradient(90deg, #a855f7, #60a5fa, #67e8f9, #60a5fa, #a855f7);
  background-size: 400%;
  background-clip: text;
  color: transparent;
  animation: holographic 5s linear infinite;
}

.animate-float-slow {
  animation: float-slow 10s ease-in-out infinite;
}

/* Smooth scrolling behavior */
html {
  scroll-behavior: smooth;
}

/* Gradient text utility */
.text-gradient {
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Enhanced selection styling */
::selection {
  background: rgba(139, 92, 246, 0.3);
  color: white;
}

/* Animations and Shadows */
@keyframes shadowGrow {
  0% {
    box-shadow: inset 0 0 20px 5px rgba(0, 0, 0, 0.5);
  }
  50% {
    box-shadow: inset 0 40px 60px rgba(0, 0, 0, 0.7);
  }
  100% {
    box-shadow: inset 0 50px 80px rgba(0, 0, 0, 0.9);
  }
}

.animate-shadowGrow {
  animation: shadowGrow 5s ease-in-out infinite alternate-reverse;
}
</style>